// src/modules/time-tracking/SiteForm.js (admin) — create/edit a geofenced site.
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaMapMarkerAlt, FaArrowLeft, FaSave, FaSpinner, FaRulerHorizontal, FaBuilding, FaCity, FaBriefcase,
  FaClock, FaExclamationTriangle,
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import Checkbox from '../../components/shared/Checkbox';
import GooglePlaceAutocomplete from '../../components/shared/GooglePlaceAutocomplete';
import {
  FormContainer, FormSection, FormHeader, BackButton, FormGrid, FormField, FieldLabel, SubmitButton,
} from '../../components/styled/FormComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import { listSites, createSite, updateSite, getTimeTrackingPrefs } from './timeTrackingService';
import { fetchJobs } from '../jobs/jobsService';

// Pull city/state/zip out of a Google PlaceResult's address_components.
const pickComponent = (place, type, key = 'long_name') =>
  place?.address_components?.find((c) => c.types.includes(type))?.[key] || '';

const SiteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '', address: '', city: '', state: '', zip_code: '',
    radius_feet: 600, latitude: '', longitude: '', is_active: true,
    // When true, employees can clock into this site with NO scheduled job —
    // for the office / yard / last-minute work. The geofence still applies.
    allow_standalone_clock_in: false,
  });
  // Optional link to an existing job — kept outside formData so it never leaks
  // into the address/coords payload logic. When set, the backend derives the
  // site's location from the job, so the address fields become optional.
  const [jobId, setJobId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  useEffect(() => {
    if (!isEditMode) return;
    const load = async () => {
      try {
        setInitialLoading(true);
        const { sites } = await listSites();
        const site = (sites || []).find((s) => String(s.id) === String(id));
        if (site) {
          setFormData({
            name: site.name || '', address: site.address || '', city: site.city || '',
            state: site.state || '', zip_code: site.zip_code || '',
            radius_feet: site.radius_feet ?? 600,
            latitude: site.latitude ?? '', longitude: site.longitude ?? '',
            is_active: site.is_active,
            allow_standalone_clock_in: !!site.allow_standalone_clock_in,
          });
          // Pre-select the linked job if the site carries one (safe if absent).
          setJobId(site.job_id || '');
        }
      } catch (e) {
        setFeedbackMessage('Failed to load site.'); setFeedbackSuccess(false); setShowFeedback(true);
      } finally { setInitialLoading(false); }
    };
    load();
  }, [id, isEditMode]);

  // On a new site, pre-fill the radius from the admin default.
  useEffect(() => {
    if (isEditMode) return;
    getTimeTrackingPrefs()
      .then((prefs) => setFormData((s) => ({ ...s, radius_feet: prefs.default_radius_feet })))
      .catch(() => {});
  }, [isEditMode]);

  // Load jobs once for the optional "attach to a job" picker (create + edit).
  useEffect(() => {
    fetchJobs()
      .then((data) => setJobs(Array.isArray(data) ? data : []))
      .catch(() => setJobs([]));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  // Fill address + city/state/zip + coordinates from a Google Places selection.
  const handleAddressSelect = (address, lat, lng, place) => {
    setFormData((s) => ({
      ...s,
      address,
      city: pickComponent(place, 'locality') || s.city,
      state: pickComponent(place, 'administrative_area_level_1', 'short_name') || s.state,
      zip_code: pickComponent(place, 'postal_code') || s.zip_code,
      latitude: lat,
      longitude: lng,
    }));
    setErrors((p) => ({ ...p, address: null }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    const hasCoords = formData.latitude !== '' && formData.longitude !== '';
    const hasAddress = [formData.address, formData.city, formData.state, formData.zip_code].some((v) => v && v.trim());
    // When a job is attached, the backend derives the location from it, so an
    // address/coordinates aren't required here.
    if (!jobId && !hasCoords && !hasAddress) e.address = 'Enter an address (to geocode) or latitude/longitude';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (Object.keys(v).length) { setErrors(v); return; }
    setLoading(true);
    try {
      const payload = {
        name: formData.name, address: formData.address, city: formData.city,
        state: formData.state, zip_code: formData.zip_code,
        radius_feet: Number(formData.radius_feet) || 600,
        allow_standalone_clock_in: !!formData.allow_standalone_clock_in,
      };
      if (formData.latitude !== '' && formData.longitude !== '') {
        payload.latitude = Number(formData.latitude);
        payload.longitude = Number(formData.longitude);
      }
      if (jobId) payload.job_id = jobId;
      if (isEditMode) {
        payload.is_active = !!formData.is_active;
        await updateSite(id, payload);
        setFeedbackMessage('Site updated successfully');
      } else {
        await createSite(payload);
        setFeedbackMessage('Site created successfully');
      }
      setFeedbackSuccess(true); setShowFeedback(true);
    } catch (error) {
      setFeedbackMessage(error.response?.data?.message || 'Failed to save site. Please try again.');
      setFeedbackSuccess(false); setShowFeedback(true);
    } finally { setLoading(false); }
  };

  if (initialLoading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/time-tracking/sites')}><FaArrowLeft /> Back to Sites</BackButton>
          <h2>{isEditMode ? 'Edit Site' : 'Add Site'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3><FaMapMarkerAlt style={{ marginRight: 8 }} />Geofenced Site</h3>
            <FormGrid>
              <FormField index={0}>
                <Input label="Name" icon={<FaBuilding />} name="name" value={formData.name}
                  onChange={handleChange} placeholder="e.g. Main Yard" error={errors.name} required />
              </FormField>
              <FormField index={1}>
                <Input label="Radius (feet)" icon={<FaRulerHorizontal />} name="radius_feet" type="number"
                  value={formData.radius_feet} onChange={handleChange} placeholder="600" />
              </FormField>
              <FormField index={2}>
                <Select label="Attach to a job (optional)" icon={<FaBriefcase />}
                  value={jobId}
                  onChange={(e) => { setJobId(e.target.value); setErrors((p) => ({ ...p, address: null })); }}>
                  <option value="">None — enter address manually</option>
                  {jobs.map((j) => (
                    <option key={j.job_id} value={j.job_id}>
                      {j.customer_name}{j.job_city ? ` — ${j.job_city}` : ''}
                    </option>
                  ))}
                </Select>
                <div style={{ color: '#888', fontSize: '0.8rem', marginTop: 4 }}>
                  Selecting a job auto-fills the location from the job — you can leave the address blank.
                </div>
              </FormField>
              <FormField index={3}>
                <FieldLabel>Address (search to auto-fill coordinates)</FieldLabel>
                <GooglePlaceAutocomplete
                  onSelect={handleAddressSelect}
                  initialValue={formData.address}
                  placeholder="Start typing an address…"
                />
                {errors.address && <div style={{ color: '#FF5252', fontSize: '0.85rem', marginTop: 4 }}>{errors.address}</div>}
              </FormField>
              <FormField index={4}>
                <Input label="City" icon={<FaCity />} name="city" value={formData.city}
                  onChange={handleChange} placeholder="City" />
              </FormField>
              <FormField index={5}>
                <Input label="State" name="state" value={formData.state}
                  onChange={handleChange} placeholder="MA" />
              </FormField>
              <FormField index={6}>
                <Input label="ZIP" name="zip_code" value={formData.zip_code}
                  onChange={handleChange} placeholder="01001" />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>Coordinates (optional — leave blank to geocode the address)</h3>
            <FormGrid>
              <FormField index={6}>
                <Input label="Latitude" name="latitude" type="number" value={formData.latitude}
                  onChange={handleChange} placeholder="42.1234" />
              </FormField>
              <FormField index={7}>
                <Input label="Longitude" name="longitude" type="number" value={formData.longitude}
                  onChange={handleChange} placeholder="-71.1234" />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3><FaClock style={{ marginRight: 8 }} />Clock-in availability</h3>
            {isEditMode && (
              <Checkbox label="Active (available for clock-in)" checked={!!formData.is_active}
                onChange={(e) => setFormData((s) => ({ ...s, is_active: e.target.checked }))} />
            )}

            <Checkbox label="Allow clock-in without a scheduled job"
              checked={!!formData.allow_standalone_clock_in}
              onChange={(e) => setFormData((s) => ({ ...s, allow_standalone_clock_in: e.target.checked }))} />
            <div style={{ color: '#888', fontSize: '0.8rem', marginTop: -4, marginLeft: 30, maxWidth: 620 }}>
              Normally an employee can only clock in when a job is scheduled for that day. Turn this on
              for a permanent location — the office, the yard, a &ldquo;General Work&rdquo; site — so crews can
              clock in for last-minute work that has no job in the system yet.
              {formData.allow_standalone_clock_in && (
                <div style={{ color: '#e0a800', marginTop: 8 }}>
                  <FaExclamationTriangle style={{ marginRight: 6 }} />
                  They must still be physically inside the {Number(formData.radius_feet) || 600} ft radius,
                  but there is no date restriction — anyone on site can start a shift on any day, weekends
                  included. Uncheck &ldquo;Active&rdquo; above to switch it off when you don&rsquo;t need it.
                </div>
              )}
            </div>
          </FormSection>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (<><FaSpinner className="icon-spin" />Saving...</>) : (<><FaSave />{isEditMode ? 'Update Site' : 'Create Site'}</>)}
          </SubmitButton>
        </form>
      </FormContainer>

      <FeedbackModal isOpen={showFeedback} message={feedbackMessage} success={feedbackSuccess}
        onComplete={() => { setShowFeedback(false); if (feedbackSuccess) navigate('/time-tracking/sites'); }} />
    </DashboardLayout>
  );
};

export default SiteForm;
